import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Alert,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccessTime,
  Group,
  CheckCircle,
  ErrorOutline,
  BarChart
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { 
  branchDashboardApi,
  type BranchDashboardStats,
  type BranchAttendanceTrend,
  type BranchEmployeePerformance,
  type BranchEmployeeStatusDistribution,
  type BranchTopPerformer
} from '../../../api/branch-dashboard.api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

interface BranchStatisticsTabProps {
  branchId: string;
  branchName: string;
}

const BranchStatisticsTab: React.FC<BranchStatisticsTabProps> = ({ branchId, branchName }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<BranchDashboardStats | null>(null);
  const [attendanceTrend, setAttendanceTrend] = useState<BranchAttendanceTrend[]>([]);
  const [employeePerformance, setEmployeePerformance] = useState<BranchEmployeePerformance[]>([]);
  const [employeeStatusDistribution, setEmployeeStatusDistribution] = useState<BranchEmployeeStatusDistribution | null>(null);
  const [topPerformers, setTopPerformers] = useState<BranchTopPerformer[]>([]);
  const [trendPeriod, setTrendPeriod] = useState<number>(30);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Paralel API çağrıları
      const [statsData, trendData, performanceData, statusDistData, topPerformersData] = await Promise.all([
        branchDashboardApi.getDashboardStats(branchId),
        branchDashboardApi.getAttendanceTrend(branchId),
        branchDashboardApi.getEmployeePerformance(branchId),
        branchDashboardApi.getEmployeeStatusDistribution(branchId),
        branchDashboardApi.getTopPerformers(branchId, 7)
      ]);
      

      setStatistics(statsData);
      setAttendanceTrend(trendData);
      setEmployeePerformance(performanceData);
      setEmployeeStatusDistribution(statusDistData);
      setTopPerformers(topPerformersData);
    } catch (err) {
      setError('İstatistikler yüklenirken bir hata oluştu');
      console.error('Statistics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleTrendPeriodChange = (event: SelectChangeEvent<number>) => {
    setTrendPeriod(Number(event.target.value));
  };

  // Chart configurations
  const attendanceChartData = {
    labels: attendanceTrend.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Devam Sayısı',
        data: attendanceTrend.map(item => item.attendanceCount),
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const attendanceChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `Son ${trendPeriod} Gün Devam Trendi`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const performanceChartData = {
    labels: employeePerformance.slice(0, 7).map(emp => emp.employeeName.split(' ')[0]),
    datasets: [
      {
        label: 'Ortalama Çalışma Saati',
        data: employeePerformance.slice(0, 7).map(emp => emp.averageWorkHours),
        backgroundColor: [
          theme.palette.success.main,
          theme.palette.info.main,
          theme.palette.warning.main,
          theme.palette.error.main,
          theme.palette.secondary.main,
          theme.palette.primary.main,
          theme.palette.grey[500],
        ],
        borderWidth: 1,
      },
    ],
  };

  const performanceChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Top 7 Çalışan Performansı',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => `${value}h`,
        },
      },
    },
  };

  const statusDistributionData = {
    labels: ['Aktif', 'Pasif'],
    datasets: [
      {
        data: employeeStatusDistribution ? [
          employeeStatusDistribution.ACTIVE,
          employeeStatusDistribution.INACTIVE,
        ] : [0, 0],
        backgroundColor: [
          theme.palette.success.main,
          theme.palette.error.main,
        ],
        borderWidth: 2,
        borderColor: theme.palette.background.paper,
      },
    ],
  };

  const statusDistributionOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Çalışan Durumu Dağılımı',
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          İstatistikler yükleniyor...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
        <Button onClick={fetchStatistics} sx={{ ml: 2 }}>
          Tekrar Dene
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <Group />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {statistics?.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Çalışan
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {statistics?.activeEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktif Çalışan
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    %{statistics?.monthlyAttendanceRate?.toFixed(1) || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aylık Devam Oranı
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                  <AccessTime />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {statistics?.averageWorkingHours?.toFixed(1) || 0}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ort. Günlük Saat
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Attendance Trend Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Devam Trendi"
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Periode</InputLabel>
                  <Select
                    value={trendPeriod}
                    label="Periode"
                    onChange={handleTrendPeriodChange}
                  >
                    <MenuItem value={7}>7 Gün</MenuItem>
                    <MenuItem value={30}>30 Gün</MenuItem>
                    <MenuItem value={90}>90 Gün</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              <Line data={attendanceChartData} options={attendanceChartOptions} />
            </CardContent>
          </Card>
        </Grid>

        {/* Status Distribution Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Çalışan Durumu" />
            <CardContent>
              <Doughnut data={statusDistributionData} options={statusDistributionOptions} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Charts and Employee List */}
      <Grid container spacing={3}>
        {/* Employee Performance Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Çalışan Performansı" />
            <CardContent>
              <Bar data={performanceChartData} options={performanceChartOptions} />
            </CardContent>
          </Card>
        </Grid>

        {/* Top Performers List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="En İyi Performans" />
            <CardContent>
              <List>
                {topPerformers.map((employee, index) => (
                  <React.Fragment key={employee.employeeId}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                          {employee.employeeName.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <div className='MuiListItemText-root MuiListItemText-multiline css-konndc-MuiListItemText-root' >
                        <span className='MuiTypography-root MuiTypography-body1 MuiListItemText-primary css-pbi6uj-MuiTypography-root'>{employee.employeeName}</span>
                        <div className='MuiTypography-root MuiTypography-body2 MuiListItemText-secondary css-gn60rs-MuiTypography-root' >
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip
                              label={`#${employee.rank || index + 1}`}
                              size="small"
                              color="primary"
                            />
                            <Chip
                              label={`${employee.averageWorkHours.toFixed(1)}h/gün`}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </div>
                      </div>
                      {/* <ListItemText
                        primary={employee.employeeName}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip
                              label={`#${employee.rank || index + 1}`}
                              size="small"
                              color="primary"
                            />
                            <Chip
                              label={`${employee.averageWorkHours.toFixed(1)}h/gün`}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        }
                      /> */}
                    </ListItem>
                    {index < 4 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BranchStatisticsTab; 